import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import IconFont from '@meitu/xdesign-icon-font';
import getClsWithPrefix from '../cls';
import './index.less';

const subPrefix = 'Select';
const cls$ = getClsWithPrefix(subPrefix);

interface Option<T extends string | number = string> {
    value: T;
    label: React.ReactNode;
}

interface SelectProps<T extends string | number = string>
    extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
    className?: string;
    popupClassName?: string;
    defaultValue?: T;
    value?: T;
    options: Option<T>[];
    width?: React.CSSProperties['width'];
    onChange?: (value: T, option: Option<T> | Array<Option<T>>) => void;
    disabled?: boolean;
}

const Select = <T extends string | number = string>({
    className,
    popupClassName,
    defaultValue,
    value,
    options,
    width,
    onChange,
    disabled,
    ...restProps
}: SelectProps<T>) => {
    const [open, setOpen] = useState(false);
    const selectElRef = useRef<HTMLDivElement>(null);
    const optionsElRef = useRef<HTMLUListElement>(null);

    // focus
    useEffect(() => {
        const onClick = (e: MouseEvent) => {
            const selectEl = selectElRef.current;
            if (!selectEl) return;
            if (!selectEl.contains(e.target as any)) {
                selectEl.dataset.focused = 'false';
                setOpen(false);
            }
        };
        document.addEventListener('click', onClick);
        return () => document.removeEventListener('click', onClick);
    }, []);

    const resetOptionsPotision = () =>
        requestAnimationFrame(() => {
            if (!selectElRef.current) return;
            if (!optionsElRef.current) return;
            const rect = selectElRef.current.getBoundingClientRect();
            optionsElRef.current.style.width = selectElRef.current.offsetWidth + 'px';
            optionsElRef.current.style.inset = `${rect.bottom}px auto auto ${rect.left}px`;
        });

    // onresize => resetOptionsPotision
    useEffect(() => {
        window.addEventListener('resize', resetOptionsPotision);
        return () => window.removeEventListener('resize', resetOptionsPotision);
    }, []);

    useEffect(() => {
        if (open) {
            resetOptionsPotision();
        }
    }, [open]);

    const handleOpen = () => {
        if (disabled) return;
        setOpen((open) => !open);
        resetOptionsPotision();
    };

    return (
        <div
            className={classNames(cls$(), className)}
            data-disabled={disabled}
            style={{
                width,
                ...restProps.style,
                ...(disabled ? { opacity: 0.6, cursor: 'not-allowed' } : {}),
            }}
            onClick={(e) => {
                if (disabled) return;
                handleOpen();
                selectElRef.current!.dataset.focused = 'true';
                restProps.onClick?.(e);
            }}
            {...restProps}
            ref={selectElRef}
        >
            {options.find((opt) => opt.value === value)?.label ?? defaultValue}
            <div className={cls$('select-icon')}>
                <IconFont name="ChevronDownBlack" color="var(--content-web-tertiary, #15171A70)" fontSize={10} />
            </div>
            {createPortal(
                <ul className={classNames(cls$('options'), popupClassName)} data-open={open} ref={optionsElRef}>
                    {options.map((opt) => {
                        const isActive = opt.value === value;
                        return (
                            <li
                                key={opt.value}
                                className={cls$('option')}
                                data-active={isActive}
                                onClick={(e) => {
                                    onChange?.(opt.value, opt);
                                    setOpen(false);
                                    e.stopPropagation();
                                }}
                            >
                                {opt.label}
                                {isActive && (
                                    <IconFont
                                        className={cls$('selected-icon')}
                                        name="CheckBold"
                                        fontSize={12}
                                        color="var(--content-tag-selected, #3388FF)"
                                    />
                                )}
                            </li>
                        );
                    })}
                </ul>,
                document.body,
            )}
        </div>
    );
};

export default Select;
